/**
 * Mobile Subscription Hook
 * 
 * React hook for subscription functionality with comprehensive error handling
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { subscriptionService, SubscriptionError } from '@/lib/subscription-service';
import { useToast } from '@/hooks/use-toast';
import type { SubscriptionProduct, UserSubscription } from '@/lib/types';

interface UseMobileSubscriptionReturn {
  // State
  products: SubscriptionProduct[];
  isLoading: boolean;
  isInitialized: boolean;
  currentSubscription: UserSubscription | null;
  isSubscribed: boolean;
  error: string | null;

  // Actions
  purchaseProduct: (productId: string, userId: string) => Promise<boolean>;
  restorePurchases: (userId: string) => Promise<number>;
  refreshStore: () => Promise<void>;
  checkSubscriptionStatus: () => Promise<void>;
  openSubscriptionManagement: () => Promise<void>;
  getSubscriptionManagementUrl: () => string | null;
  clearError: () => void;
}

export const useMobileSubscription = (userId?: string | null): UseMobileSubscriptionReturn => {
  const [products, setProducts] = useState<SubscriptionProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(
    subscriptionService.getCurrentSubscription()
  );
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const initAttempted = useRef(false);
  const userIdRef = useRef<string | null>(null);

  /**
   * Listen for subscription updates from the service
   */
  useEffect(() => {
    const unsubscribe = subscriptionService.subscribeToSubscriptionChanges((subscription) => {
      setCurrentSubscription(subscription);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  /**
   * Initialize subscription service on mount and when user changes
   */
  useEffect(() => {
    const initializeService = async () => {
      // Only initialize once or when userId changes
      if (initAttempted.current && userIdRef.current === userId) {
        return;
      }

      initAttempted.current = true;
      userIdRef.current = userId || null;

      try {
        setIsLoading(true);
        setError(null);

        // Initialize with userId if available
        await subscriptionService.initialize(userId || undefined);
        
        // Set userId in RevenueCat if user is logged in
        if (userId) {
          await subscriptionService.setUserId(userId);
        }
        
        setIsInitialized(true);

        // Load products
        const availableProducts = await subscriptionService.getProducts();
        setProducts(availableProducts);

        // Check subscription status (now with correct userId)
        const subscription = await subscriptionService.checkSubscriptionStatus();
        setCurrentSubscription(subscription);
      } catch (err) {
        // Log error for debugging but don't show to users
        // Subscription service initialization failures are not user-facing errors
        console.error('[useMobileSubscription] Initialization failed:', err);
        
        const errorMessage = err instanceof SubscriptionError 
          ? err.message 
          : 'Failed to initialize subscription service';
        
        // Store error internally for debugging, but don't show to users
        setError(errorMessage);
        
        // Don't show toast - subscription initialization errors are technical
        // and not helpful to end users. The app will gracefully handle the
        // case where subscription features are unavailable.
      } finally {
        setIsLoading(false);
      }
    };

    initializeService();
  }, [userId]);

  /**
   * Purchase a product
   */
  const purchaseProduct = useCallback(async (productId: string, userId: string): Promise<boolean> => {
    if (!isInitialized) {
      toast({
        title: 'Not Ready',
        description: 'Subscription service is initializing. Please wait...',
        variant: 'destructive'
      });
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Ensure user ID is set in RevenueCat
      await subscriptionService.setUserId(userId);

      const transaction = await subscriptionService.purchaseProduct(productId);
      
      if (transaction) {
        // Set userId for the transaction
        transaction.userId = userId;

        toast({
          title: 'Purchase Successful',
          description: 'Your subscription has been activated!',
        });

        // Wait a moment for backend sync to complete
        await new Promise(resolve => setTimeout(resolve, 500));

        // Refresh subscription status from RevenueCat
        // This ensures we get the latest status after the purchase
        const subscription = await subscriptionService.checkSubscriptionStatus();
        if (subscription) {
          subscription.userId = userId;
          setCurrentSubscription(subscription);
        } else {
          // If no subscription found, try refreshing store data and check again
          await subscriptionService.refreshStore();
          const refreshedSubscription = await subscriptionService.checkSubscriptionStatus();
          if (refreshedSubscription) {
            refreshedSubscription.userId = userId;
            setCurrentSubscription(refreshedSubscription);
          }
        }

        // Force a re-check to ensure UI updates
        // The AuthContext useEffect will pick up the status change
        return true;
      }

      return false;
    } catch (err) {
      console.error('[useMobileSubscription] Purchase error:', err);

      let errorMessage = 'An error occurred during purchase';
      let shouldShowToast = true;

      if (err instanceof SubscriptionError) {
        switch (err.code) {
          case 'USER_CANCELLED':
            errorMessage = 'Purchase was cancelled';
            shouldShowToast = false; // Don't show toast for user cancellation
            break;
          case 'TIMEOUT':
            errorMessage = 'Purchase timed out. Please try again.';
            break;
          case 'PRODUCT_NOT_FOUND':
            errorMessage = 'Product not available. Please try again later.';
            break;
          case 'PURCHASE_FAILED':
            errorMessage = 'Purchase failed. Please check your payment method.';
            break;
          default:
            errorMessage = err.message;
        }
      }

      setError(errorMessage);

      if (shouldShowToast) {
        toast({
          title: 'Purchase Error',
          description: errorMessage,
          variant: 'destructive'
        });
      }

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, toast]);

  /**
   * Restore previous purchases
   */
  const restorePurchases = useCallback(async (userId: string): Promise<number> => {
    if (!isInitialized) {
      toast({
        title: 'Not Ready',
        description: 'Subscription service is initializing. Please wait...',
        variant: 'destructive'
      });
      return 0;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Ensure user ID is set in RevenueCat
      await subscriptionService.setUserId(userId);

      const transactions = await subscriptionService.restorePurchases();

      // Set userId for all transactions
      transactions.forEach(transaction => {
        transaction.userId = userId;
      });

      if (transactions.length > 0) {
        toast({
          title: 'Purchases Restored',
          description: `Found ${transactions.length} previous purchase(s).`,
        });

        // Update current subscription
        const subscription = await subscriptionService.checkSubscriptionStatus();
        if (subscription) {
          subscription.userId = userId;
          setCurrentSubscription(subscription);
        }

        // Note: User profile refresh is handled by AuthProvider via useEffect
        // that watches for subscription status changes
      } else {
        toast({
          title: 'No Purchases Found',
          description: 'No previous purchases were found to restore.',
        });
      }

      return transactions.length;
    } catch (err) {
      console.error('[useMobileSubscription] Restore error:', err);

      const errorMessage = err instanceof SubscriptionError
        ? err.message
        : 'Failed to restore purchases';

      setError(errorMessage);

      toast({
        title: 'Restore Error',
        description: errorMessage,
        variant: 'destructive'
      });

      return 0;
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, toast]);

  /**
   * Refresh store data
   */
  const refreshStore = useCallback(async () => {
    if (!isInitialized) {
      return;
    }

    try {
      setError(null);
      await subscriptionService.refreshStore();
      
      const availableProducts = await subscriptionService.getProducts();
      setProducts(availableProducts);

      const subscription = await subscriptionService.checkSubscriptionStatus();
      setCurrentSubscription(subscription);
    } catch (err) {
      console.error('[useMobileSubscription] Refresh error:', err);
      
      const errorMessage = err instanceof SubscriptionError
        ? err.message
        : 'Failed to refresh store';

      setError(errorMessage);
    }
  }, [isInitialized]);

  /**
   * Check subscription status
   */
  const checkSubscriptionStatus = useCallback(async () => {
    if (!isInitialized) {
      return;
    }

    try {
      setError(null);
      
      // Ensure userId is set before checking status
      if (userId) {
        await subscriptionService.setUserId(userId);
      }
      
      const subscription = await subscriptionService.checkSubscriptionStatus();
      setCurrentSubscription(subscription);
    } catch (err) {
      console.error('[useMobileSubscription] Status check error:', err);
      
      const errorMessage = err instanceof SubscriptionError
        ? err.message
        : 'Failed to check subscription status';

      setError(errorMessage);
    }
  }, [isInitialized, userId]);

  /**
   * Open subscription management
   */
  const openSubscriptionManagement = useCallback(async () => {
    try {
      setError(null);
      await subscriptionService.openSubscriptionManagement();
    } catch (err) {
      console.error('[useMobileSubscription] Open management error:', err);
      
      toast({
        title: 'Error',
        description: 'Unable to open subscription management',
        variant: 'destructive'
      });
    }
  }, [toast]);

  /**
   * Get subscription management URL
   */
  const getSubscriptionManagementUrl = useCallback(() => {
    return subscriptionService.getSubscriptionManagementUrl();
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    products,
    isLoading,
    isInitialized,
    currentSubscription,
    isSubscribed: currentSubscription?.status === 'active',
    error,
    purchaseProduct,
    restorePurchases,
    refreshStore,
    checkSubscriptionStatus,
    openSubscriptionManagement,
    getSubscriptionManagementUrl,
    clearError
  };
};

